import { Component, computed, effect, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { finalize, map, switchMap } from 'rxjs/operators';

import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';

import { AsyncPipe, NgClass } from '@angular/common';
import { isOsVersionAtLeast } from '@rocksoft/shared';
import { AppleCommandProgressService } from '../../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../../utilities/command-spinner/command-spinner.component';
import { AppleDevice } from '../../../../viewmodels/apple/apple-device.model';
import { CommandProgress } from '../../../../viewmodels/apple/signalr/command-context.model';

@Component({
  selector: 'ml-mdm-play-lost-mode-sound',
  templateUrl: './play-lost-mode-sound.component.html',
  imports: [CommandSpinnerComponent, NgClass, AsyncPipe],
})
export class PlayLostModeSoundComponent {
  private readonly appleMdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);

  readonly apple = input.required<AppleDevice>();
  readonly class = input.required<string>();

  private _isProcessing = new BehaviorSubject(false);
  isEnabled: Observable<boolean | null>;

  readonly isLostModeEnabled = computed(() => this.apple().isMDMLostModeEnabled ?? false);
  readonly isSupervised = computed(() => this.apple()?.isSupervised ?? false);
  readonly hasSupportedOS = computed(() => isOsVersionAtLeast(this.apple().osVersion, '9.3.0'));

  private readonly _udidSignal = computed(() => this.apple().udid);
  private _commandProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  readonly commandProgress$: Observable<CommandProgress> = toObservable(this._udidSignal).pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'PlayLostModeSound'))
    )
  );

  enabled = true;

  get isProcessing(): Observable<boolean> {
    return this._isProcessing;
  }

  constructor() {
    this.isEnabled = this.isProcessing.pipe(
      map((isProcessing) => {
        return (
          this.isSupervised() &&
          this.isLostModeEnabled() &&
          !isProcessing &&
          (this.hasSupportedOS() == null || this.hasSupportedOS())
        );
      })
    );

    effect(() => {
      this._isProcessing.next(false);
    });
  }

  playLostModeSound() {
    const udid = this.apple().udid;
    if (!this.isEnabled) {
      return;
    }

    this._isProcessing.next(true);
    const command: CommandProgress = {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'PlayLostModeSound',
    };
    this._commandProgress.next(command);
    this.appleMdmService
      .playLostModeSound(udid)
      .pipe(
        finalize(() => {
          this._isProcessing.next(false);
        })
      )
      .subscribe();
  }
}
